import React from 'react';
import { Inputs, PackageType, IndustryType, RevenueCadence, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
interface AssumptionsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
}
export function AssumptionsSidebar({
  isOpen,
  onClose,
  inputs,
  onChange
}: AssumptionsSidebarProps) {
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    onChange({
      ...inputs,
      industry: newIndustry,
      ticket: defaults.ticket,
      gpm: defaults.gpm,
      salesFirst90Total: defaults.salesFirst90Total,
      salesVetMonthly: defaults.salesVetMonthly,
      revenueCadence: defaults.revenueCadence,
      residualTicket: defaults.residualTicket
    });
  };
  return <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} data-id="element-1" data-mp="3hbh0epdse8ip" />}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2" data-mp="37mpb6p0422bh">
        <div className="h-full flex flex-col" data-id="element-3" data-mp="3xdnjwlzvqeg">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-navy" data-id="element-4" data-mp="3zgld2te2ntq">
            <h2 className="text-lg font-bold text-white" data-id="element-5" data-mp="dr9qvyyug3kd">
              Business Assumptions
            </h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-id="element-6" data-mp="4sxokzom7hc3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7" data-mp="3qzvza13josw3">
                <line x1="18" y1="6" x2="6" y2="18" data-id="element-8" data-mp="3d8zm9im3qka5" />
                <line x1="6" y1="6" x2="18" y2="18" data-id="element-9" data-mp="3daald54lr3ux" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4" data-id="element-10" data-mp="33atmb6juvo7x">
            <div className="space-y-4" data-id="element-11" data-mp="33bbo0ipaeun8">
              {/* Company Info */}
              <div className="space-y-3" data-id="element-12" data-mp="2t5tm6pt976mx">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-13" data-mp="2t8zai2u264hl">
                  Company Info
                </h3>

                <div data-id="element-14" data-mp="334j4uksz4v48">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-15" data-mp="3363zsbj7ag2m">
                    Company Name
                  </label>
                  <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-16" data-mp="2ag57m79pm2tq" />
                </div>

                <div data-id="element-17" data-mp="2p7496yc4nr3t">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-18" data-mp="1rpvtq04k8mm7">
                    Industry
                  </label>
                  <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-19" data-mp="1wnfjnxamentz">
                    <option value="" data-id="element-20" data-mp="c04qhb5863sh">Select</option>
                    <option data-id="element-21" data-mp="76jxxymei20o">Windows</option>
                    <option data-id="element-22" data-mp="75s34q0mo8bg">Roofing</option>
                    <option data-id="element-23" data-mp="ky8ag30kasi6">Solar (Sales)</option>
                    <option data-id="element-24" data-mp="l12uhtfnybb2">Solar (Vertical)</option>
                    <option data-id="element-25" data-mp="gwcze6stx0r7">Solar (Community)</option>
                    <option data-id="element-26" data-mp="gzr07aluh1wj">Pest</option>
                    <option data-id="element-27" data-mp="uxfkohc02712">Fiber</option>
                    <option data-id="element-28" data-mp="uv386jp0gove">Deregulated Energy</option>
                    <option data-id="element-29" data-mp="pxtayhvdr8x0">Hardscaping</option>
                    <option data-id="element-30" data-mp="pvzzmp9to0io">Landscaping</option>
                    <option data-id="element-31" data-mp="2wnr4sa75ge27">Other</option>
                  </select>
                </div>

                <div data-id="element-32" data-mp="2rpz9r7b7kbts">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-33" data-mp="2rqr8us40krot">
                    Partnership
                  </label>
                  <select value={inputs.pkg} onChange={e => handleChange('pkg', e.target.value as PackageType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none font-semibold" data-id="element-34" data-mp="179riy5ec8qzz">
                    <option value="" data-id="element-35" data-mp="ofw0pt5xxgol">Select</option>
                    <option value="edge" data-id="element-36" data-mp="oja2az6s5luz">HIRE Edge</option>
                    <option value="advantage" data-id="element-37" data-mp="tgs1hxuzwmps">HIRE Advantage</option>
                    <option value="summit" data-id="element-38" data-mp="ej9f325silli">HIRE Summit</option>
                  </select>
                </div>
              </div>

              {/* Campaign & Ad Spend */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-39" data-mp="jjnooon1i3ln">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-40" data-mp="28xk5hf219nte">
                  Campaign & Ad Spend
                </h3>

                <div data-id="element-41" data-mp="2ceeziy62x4vx">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-42" data-mp="2cfpzso0bfuia">
                    Number of Campaigns
                  </label>
                  <input type="number" min="0.5" step="0.5" value={inputs.campaigns} onChange={e => handleNumericChange('campaigns', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-43" data-mp="27hy9tzj8eigq" />
                </div>

                <div data-id="element-44" data-mp="2lebg0cibexfw">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-45" data-mp="2ldtilcjx2d6e">
                    Ad Spend per Month (per campaign)
                  </label>
                  <div className="relative" data-id="element-46" data-mp="3dvxi91a20lop">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-47" data-mp="3dxs54l4vynvf">
                      $
                    </span>
                    <input type="number" min="0" step="100" value={inputs.adSpend} onChange={e => handleNumericChange('adSpend', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-48" data-mp="vn31u2eq7z3x" />
                  </div>
                </div>

                <div data-id="element-49" data-mp="1jjgs43ji6y8c">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-50" data-mp="1jiyxtcgeiu32">
                    Ad Cost per Hire
                  </label>
                  <div className="relative" data-id="element-51" data-mp="1sckcywwhadd0">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-52" data-mp="1sbac5zy8qr2o">
                      $
                    </span>
                    <input type="number" min="1" step="10" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-53" data-mp="22arhuhdht4yn" />
                  </div>
                </div>
              </div>

              {/* Revenue & Margins */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-54" data-mp="ui5zujgwqmkv">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-55" data-mp="pijoygojxif8">
                  Revenue & Margins
                </h3>

                <div data-id="element-56" data-mp="13azk6vb7oc5l">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-57" data-mp="13b9cipjqaveg">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-58" data-mp="asc7o4aa65g4">
                    <option value="one-time" data-id="element-59" data-mp="3iwczo4zcfbkp">One-Time Sale</option>
                    <option value="monthly" data-id="element-60" data-mp="3nt3k14i6ktbx">Monthly Residual</option>
                    <option value="quarterly" data-id="element-61" data-mp="fe393nb2o1bf">Quarterly Residual</option>
                  </select>
                </div>

                <div data-id="element-62" data-mp="kdpkrsb959i4">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-63" data-mp="kbv2ymva1xi9">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket' : 'Initial Sale Ticket'}
                  </label>
                  <div className="relative" data-id="element-64" data-mp="2q6e7w1trl0cc">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-65" data-mp="2q5ctbndjhvpv">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-66" data-mp="362osndviizi9" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <div data-id="element-67" data-mp="2scl9wwvudnxk">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-68" data-mp="2s8npm7xj2qc3">
                      {inputs.revenueCadence === 'monthly' ? 'Monthly Residual' : 'Quarterly Residual'}
                    </label>
                    <div className="relative" data-id="element-69" data-mp="bno4pe7xa91o">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-70" data-mp="glhfylo31t8t">
                        $
                      </span>
                      <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-71" data-mp="2141pw42g5uh9" />
                    </div>
                  </div>}

                {inputs.revenueCadence === 'monthly' && <div data-id="element-72" data-mp="2tq36c7rowiga">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-73" data-mp="2ot2ybrba55mj">
                      Monthly Attrition
                    </label>
                    <div className="relative" data-id="element-74" data-mp="zeoenlf4c8xw">
                      <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-75" data-mp="ufbgw5mplhgj" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-76" data-mp="3ieqhfiiyr50i">
                        %
                      </span>
                    </div>
                  </div>}

                {inputs.revenueCadence === 'quarterly' && <div data-id="element-77" data-mp="1iwy3jwax81">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-78" data-mp="225wurkib9x">
                      Quarterly Attrition
                    </label>
                    <div className="relative" data-id="element-79" data-mp="4y0wmp0dc5di">
                      <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-80" data-mp="f5bw57p1f39o" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-81" data-mp="o3nujvw5b5co">
                        %
                      </span>
                    </div>
                  </div>}

                <div data-id="element-82" data-mp="1gne1v5xirync">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-83" data-mp="1bukzgopix2ef">
                    Gross Profit Margin
                  </label>
                  <div className="relative" data-id="element-84" data-mp="wbryby8mryz4">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-85" data-mp="11bmtidzfvapz" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-86" data-mp="1k1ck0ow65ac3">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Sales Performance */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-87" data-mp="1y1nbnu5aysyb">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-88" data-mp="22sdilbtdn3v4">
                  Sales Performance
                </h3>

                <div data-id="element-89" data-mp="3nhpy1cww7rji">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-90" data-mp="3nh7r9mcltuxc">
                    Sales per 90 Day Rep
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-91" data-mp="13psm7flbqex" />
                </div>

                <div data-id="element-92" data-mp="3efzz7m0olqbp">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-93" data-mp="39iqbcasxxmpz">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-94" data-mp="tqambejjgeln" />
                </div>

                <div data-id="element-95" data-mp="1asgcljnn2mfy">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-96" data-mp="1aszks8nhlwgs">
                    Pullthrough / Realization
                  </label>
                  <div className="relative" data-id="element-97" data-mp="i4aqasgxspe0">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-98" data-mp="i53pn8dkgcm4" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-99" data-mp="2cnb2fy60gvq5">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Retention Model */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-100" data-mp="1yp369sgstyg1">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-101" data-mp="1yqxkyp1h8ubo">
                  Retention Model
                </h3>

                <div data-id="element-102" data-mp="1oyrrfavku4ud">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-103" data-mp="1oy89vv9u9igv">
                    Hired to Launched
                  </label>
                  <div className="relative" data-id="element-104" data-mp="1fybruchyfhus">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-105" data-mp="1fzmta5nxgdiq" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-106" data-mp="2kwyotxrmkchj">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-107" data-mp="2b641sr6k18v1">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-108" data-mp="2b4sw2ltjtr1a">
                    Launched to Day 30
                  </label>
                  <div className="relative" data-id="element-109" data-mp="225xnx46y26vc">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-110" data-mp="2278sh687rini" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-111" data-mp="19jzcievirlqs">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-112" data-mp="1ijmccw8lojhq">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-113" data-mp="1ij45l5obamvk">
                    Day 30 to Day 90
                  </label>
                  <div className="relative" data-id="element-114" data-mp="1si1x8hh1awis">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-115" data-mp="1shaazx9bub56" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-116" data-mp="2nqi1k07g415g">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-117" data-mp="2ws9631q921xq">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-118" data-mp="2wqocpffo8p1g">
                    Day 90 to 1 Year
                  </label>
                  <div className="relative" data-id="element-119" data-mp="250ta4h2sz2uu">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-120" data-mp="251l6hu4854m6" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-121" data-mp="1hbqwrlf7bw65">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}